"use client"

import ButtonWithArrow from "@/ui/common/bit/ButtonWithArrow"
import CommunityTexts from "@/ui/common/bit/CommunityTexts"
import { extractBlockProperties } from "@/utils/simplifyData"
import { handleNumericInput } from "@/utils/utils"
import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import { Box, CircularProgress, TextField } from "@mui/material"
import axios, { AxiosError } from "axios"
import { useTranslations } from "next-intl"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { colors, mediaQueries, sizes } from "ui"
import { submitClick, submitError } from "utils/gtm"
import * as yup from "yup"
import BlockWrapper from "../../common/BlockWrapper"

const StyledContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: ${sizes.baseDouble} 0;

  ${mediaQueries.desktopM} {
    padding: ${sizes.baseQuadric} 0;
  }
`

const StyledFormContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: ${sizes.baseSepta} ${sizes.baseQuadric};
  background-color: ${colors.white};
  border-radius: ${sizes.borderRadiusLarge};
  box-shadow: ${colors.dropShadow.main};
  form {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: ${sizes.baseTriple};
  }
  ${mediaQueries.tabletDown} {
    margin-top: ${sizes.baseSix};
  }
  ${mediaQueries.desktopM} {
    margin-top: ${sizes.baseSepta};
    padding: ${sizes.baseSix} ${sizes.baseQuadric};
    max-width: 736px;
  }
  ${mediaQueries.desktopXl} {
    margin-top: ${sizes.baseDeca};
  }
`
const TextFieldsWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 20px;
  ${mediaQueries.desktopM} {
    flex-direction: row;
  }
`

const StyledTextField = styled(TextField)`
  .MuiInputBase-root {
    border-radius: ${sizes.baseHalf};
    height: ${sizes.baseSepta};
  }
  .MuiOutlinedInput-root {
    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: ${colors.borderGray};
    }
  }
  input::placeholder {
    font-size: 16px;
  }
`

const StyledFormTitles = styled(CommunityTexts)`
  align-items: center;
  text-align: center;
  gap: ${sizes.baseTriple};
`

const StyledButton = styled(ButtonWithArrow)`
  margin-top: ${sizes.baseSingle};
  padding: ${sizes.baseSingle} ${sizes.baseDouble};
  align-self: center;
`

const StyledFeedback = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin-top: ${sizes.baseDouble};
  padding: ${sizes.baseSingle};
  gap: ${sizes.baseTriple};
  border-radius: ${sizes.baseHalf};
`

const StyledSuccessFeedback = styled(StyledFeedback)``

interface VerifyProjectProps {
  data: any
  id?: string
}

interface FormValues {
  failTitle: any
  failText: any
  failButton: any
  successTitle: any
  successText: any
  successButton: any
  accountNumber: string
  bankCode: string
  bankBranchNumber: string
}

const VerifyProject = ({ data, id }: VerifyProjectProps) => {
  const t = useTranslations("VerifyProject")

  const [formState, setFormState] = useState<"idle" | "loading" | "done">(
    "idle",
  )
  const [responseTitle, setResponseTitle] = useState<string>("")
  const [responseText, setResponseText] = useState<string>("")
  const [responseButtonText, setResponseButtonText] = useState<string>("")
  const currData = { ...data, button: undefined, buttonPrevious: data.button }
  const responseTexts = extractBlockProperties(data?.projectItem?.blocks)[0]

  // Form validation schema
  const schema = yup.object().shape({
    accountNumber: yup
      .string()
      .required(t("validation.accountRequired"))
      .matches(/^[0-9]+$/, t("validation.accountMustBeNumber"))
      .min(6, t("validation.bankCodeMustBeNumber"))
      .max(9, t("validation.bankCodeMustBeNumber")),
    bankCode: yup
      .string()
      .required(t("validation.bankCodeRequired"))
      .matches(/^[0-9]+$/, t("validation.bankCodeMustBeNumber"))
      .min(1, t("validation.bankCodeMustBeNumber"))
      .max(3, t("validation.bankCodeMustBeNumber")),
    bankBranchNumber: yup
      .string()
      .required(t("validation.branchRequired"))
      .matches(/^[0-9]+$/, t("validation.branchMustBeNumber"))
      .min(1, t("validation.bankCodeMustBeNumber"))
      .max(3, t("validation.bankCodeMustBeNumber")),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
  })

  const onSubmit = async (formData: FormValues) => {
    try {
      setFormState("loading")
      submitClick(t("trackingLabel"), t("trackingAction"))
      // Use our local API route instead of calling the external API directly
      const response = await axios.post("/api/verify-project", formData)

      if (response.status === 200) {
        setFormState("done")
        if (response?.data?.body?.isAccountExist === true) {
          setResponseTitle(responseTexts?.successTitle)
          setResponseText(responseTexts?.successText)
          setResponseButtonText(responseTexts?.successButton?.text)
        } else {
          setResponseTitle(responseTexts?.failTitle)
          setResponseText(responseTexts?.failText)
          setResponseButtonText(responseTexts?.failButton?.text)
        }

        reset()
      } else {
        console.error("Failed to verify project")
      }
    } catch (error) {
      console.error("Error submitting form:", error)
      const axiosError = error as AxiosError<{ error: string }>
      setFormState("done")
      submitError(t("trackingAction"), t("trackingLabel"), t("trackingError"))
      setResponseTitle(t("errorTitle"))
      setResponseText(axiosError.response?.data?.error || t("errorUnknown"))
      setResponseButtonText(responseTexts?.failButton?.text)
    }
  }

  // Prepare the CommunityTexts data object for the main component
  const mainCommunityTextsData = {
    title: currData.title,
    subtitle: currData.subtitle,
    text: currData.text,
    hasExpandButton: currData.hasExpandButton,
    button: currData.button,
    buttonPrevious: currData.buttonPrevious,
    additionalButton: currData.additionalButton,
    ArrowIcon: currData.ArrowIcon,
  }

  // Prepare the CommunityTexts data object for the form titles
  const formTitlesData = {
    subtitle: responseTexts.title,
    text: responseTexts.text,
  }

  // Prepare the CommunityTexts data object for feedback
  const feedbackData = {
    subtitle: responseTitle,
    text: responseText,
  }

  return (
    <BlockWrapper id={id}>
      <StyledContainer>
        <CommunityTexts data={mainCommunityTextsData} />
        <StyledFormContainer>
          {(formState === "idle" || formState === "loading") && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <StyledFormTitles data={formTitlesData} />
              <TextFieldsWrapper>
                <StyledTextField
                  {...register("accountNumber")}
                  placeholder={
                    responseTexts?.placeholderAccount ||
                    t("placeholders.account")
                  }
                  type="text"
                  error={!!errors.accountNumber}
                  helperText={errors.accountNumber?.message}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    maxLength: 9,
                    minLength: 5,
                  }}
                  onInput={handleNumericInput}
                  fullWidth
                  required
                />

                <StyledTextField
                  {...register("bankBranchNumber")}
                  placeholder={
                    responseTexts?.placeholderBranch || t("placeholders.branch")
                  }
                  type="text"
                  error={!!errors.bankBranchNumber}
                  helperText={errors.bankBranchNumber?.message}
                  required
                  fullWidth
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    maxLength: 3,
                    minLength: 1,
                  }}
                  onInput={handleNumericInput}
                />
                <StyledTextField
                  {...register("bankCode")}
                  placeholder={
                    responseTexts?.placeholderBankCode ||
                    t("placeholders.bankCode")
                  }
                  type="text"
                  error={!!errors.bankCode}
                  helperText={errors.bankCode?.message}
                  required
                  fullWidth
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    maxLength: 3,
                    minLength: 1,
                  }}
                  onInput={handleNumericInput}
                />
              </TextFieldsWrapper>
              <StyledButton type="submit" disabled={formState === "loading"}>
                {formState === "loading" ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t("button")
                )}
              </StyledButton>
            </form>
          )}
          {formState === "done" && (
            <StyledSuccessFeedback>
              <StyledFormTitles data={feedbackData} />
              <ButtonWithArrow onClick={() => setFormState("idle")}>
                {responseButtonText}
              </ButtonWithArrow>
            </StyledSuccessFeedback>
          )}
        </StyledFormContainer>
      </StyledContainer>
    </BlockWrapper>
  )
}

export default VerifyProject
